import moment from "moment";

/**
 * When in the recipient's day a campaign DM goes out (PS-5765): either one
 * fixed time, or spread across a window so a big list doesn't land on one org
 * all at once. Times are "HH:mm" in each recipient's own timezone. Kept free
 * of DB imports: the admin UI imports it too.
 */

/** Default time of day for sending DM campaigns (11 AM) */
export const DEFAULT_SEND_HOUR = 11;
export const DEFAULT_SEND_TIME = "11:00";

/** "HH:mm", 24-hour */
export const SEND_TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

/**
 * Window slots are this far apart because the send job only runs every 30
 * minutes (DM_CAMPAIGN_MESSAGES_CRON_JOB_FREQUENCY_MINUTES); finer slots would
 * still go out together.
 */
export const SEND_SLOT_MINUTES = 30;

export interface LocalTime {
  hour: number;
  minute: number;
}

export interface SendWindow {
  /** "HH:mm", first slot */
  start: string;
  /** "HH:mm", last possible slot */
  end: string;
}

export interface SendScheduleOptions {
  sendTime?: string;
  sendWindow?: SendWindow;
}

function toMinutes(time: string): number | null {
  const match = SEND_TIME_PATTERN.exec(time || "");
  if (!match) {
    return null;
  }
  return parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
}

/** Falls back to 11:00 when no valid time was given. */
export function parseSendTime(sendTime?: string): LocalTime {
  const minutes = sendTime ? toMinutes(sendTime) : null;
  if (minutes === null) {
    return { hour: DEFAULT_SEND_HOUR, minute: 0 };
  }
  return { hour: Math.floor(minutes / 60), minute: minutes % 60 };
}

/** Both ends are valid times and the window is not empty or backwards. */
export function isValidSendWindow(window: any): window is SendWindow {
  if (!window || typeof window !== "object") {
    return false;
  }
  const start = toMinutes(window.start);
  const end = toMinutes(window.end);
  return start !== null && end !== null && end > start;
}

/** Every SEND_SLOT_MINUTES from start through end, inclusive. */
export function buildWindowSlots(window: SendWindow): LocalTime[] {
  const start = toMinutes(window.start);
  const end = toMinutes(window.end);
  const slots: LocalTime[] = [];
  for (let m = start; m <= end; m += SEND_SLOT_MINUTES) {
    slots.push({ hour: Math.floor(m / 60), minute: m % 60 });
  }
  return slots;
}

/**
 * Returns a function that gives each recipient their send time. With a
 * window, recipients are dealt out across the slots round-robin within each
 * org, so every org is spread evenly however the list is sorted. Each org
 * starts at a random slot: the admin UI sends a big list as several requests
 * (one per 5,000-user batch, or per date in CSV range mode), each with its own
 * picker, and starting them all at the first slot would pile the remainders
 * onto the earliest times. Without a window, everyone gets the fixed time
 * (11:00 by default).
 */
export function createSendTimePicker(
  options: SendScheduleOptions = {},
  random: () => number = Math.random,
): (orgId: string) => LocalTime {
  if (options.sendWindow && isValidSendWindow(options.sendWindow)) {
    const slots = buildWindowSlots(options.sendWindow);
    const nextIndexByOrg = new Map<string, number>();
    return (orgId: string) => {
      const index = nextIndexByOrg.has(orgId)
        ? nextIndexByOrg.get(orgId)
        : Math.floor(random() * slots.length);
      nextIndexByOrg.set(orgId, index + 1);
      return slots[index % slots.length];
    };
  }
  const fixed = parseSendTime(options.sendTime);
  return () => fixed;
}

/** "2:30 PM" */
export function formatLocalTime(time: LocalTime): string {
  return moment({ hour: time.hour, minute: time.minute }).format("h:mm A");
}

/** "2:30 PM" or "spread from 10:00 AM to 3:00 PM" */
export function describeSendSchedule(
  options: SendScheduleOptions = {},
): string {
  if (options.sendWindow && isValidSendWindow(options.sendWindow)) {
    return `spread from ${formatLocalTime(
      parseSendTime(options.sendWindow.start),
    )} to ${formatLocalTime(parseSendTime(options.sendWindow.end))}`;
  }
  return formatLocalTime(parseSendTime(options.sendTime));
}
